// URL <-> language routing helpers.
//
// The URL is the ONLY source of truth for which language to render:
//   - /             → nl
//   - /support      → nl
//   - /en           → en
//   - /en/          → en
//   - /en/support   → en
//
// localStorage may remember a user's preference so the switcher can be sticky
// across sessions, but it MUST NOT decide what gets rendered on any given URL.
// A crawler that fetches / must ALWAYS get nl, regardless of Accept-Language.

import { BLOG_EN_SLUGS } from "../content/blogEnSlugs";

export const SITE = "https://ghio.nl";

/** Return "en" if pathname is /en or begins with /en/, else "nl". */
export function langFromPath(pathname) {
  if (!pathname) return "nl";
  if (pathname === "/en" || pathname === "/en/") return "en";
  if (pathname.startsWith("/en/")) return "en";
  return "nl";
}

/** Strip the /en prefix. "/en/support" → "/support". "/en" → "/". */
export function stripEnPrefix(pathname) {
  if (!pathname) return "/";
  if (pathname === "/en" || pathname === "/en/") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
}

/** Add the /en prefix. "/support" → "/en/support". "/" → "/en/". */
export function addEnPrefix(pathname) {
  if (!pathname || pathname === "/") return "/en/";
  return `/en${pathname}`;
}

/** Blog slugs that have a real English translation (body AND meta). */
export const EN_BLOG_SLUGS = BLOG_EN_SLUGS;

/** Does a blog slug have a real EN translation? */
export function blogHasEn(slug) {
  return BLOG_EN_SLUGS.has(slug);
}

/**
 * Return the counterpart URL when the user clicks the NL/EN switcher.
 *
 * Rules:
 *   - Switching to the same language returns the same path (no-op).
 *   - Blog post → EN: if slug has no EN body, fall back to /en/blog (index).
 *   - Blog post → NL: NL versions always exist, keep the slug.
 *   - Everything else: just add/strip /en.
 *
 * Note: /en/blog/:slug that has no EN body will 301-redirect at render time
 * to /blog/:slug (see BlogPostPage). That is a client-side redirect because
 * this is a SPA — a real 301 requires hosting-level rewrite rules.
 */
export function counterpartPath(pathname, targetLang) {
  const currentLang = langFromPath(pathname);
  if (currentLang === targetLang) return pathname;

  const nlPath = currentLang === "en" ? stripEnPrefix(pathname) : pathname;

  // Blog post special case
  const blogMatch = nlPath.match(/^\/blog\/([^/]+)\/?$/);
  if (blogMatch && targetLang === "en") {
    const slug = blogMatch[1];
    if (!blogHasEn(slug)) return "/en/blog";
    return `/en/blog/${slug}`;
  }

  const caseNl = nlPath.match(/^\/(?:werk|work)\/([^/]+)\/?$/);
  if (caseNl) return targetLang === "en" ? `/en/work/${caseNl[1]}` : `/werk/${caseNl[1]}`;

  return targetLang === "en" ? addEnPrefix(nlPath) : nlPath;
}

/** Showcase URL for a case slug. */
export const casePath = (slug, lang) => (lang === "en" ? `/en/work/${slug}` : `/werk/${slug}`);
